/**
 * CaseScreenshotsSection — the read-only "AtWork Confirmation Screenshots"
 * grid on File Detail (decomposition step F4, CLAUDE.md §8.4): one cell per
 * uploaded case screenshot, thumbnail opening the lightbox, case tag and
 * label. The active upload UX is the CaseScreenshotsPanel in the sidebar;
 * this is the audit-trail rendering in the main column.
 *
 * Props: `file` (for `caseLabels`), `screenshotsByIndex` (from
 * useCaseScreenshots) and `onOpenLightbox(url)`. The view's guard
 * `{hasCases && caseScreenshots.length > 0 && (…)}` stays at the call
 * site, and the lightbox itself renders at the end of the view, so the
 * DOM order is unchanged. Extracted verbatim from FileDetail.jsx apart
 * from the lightbox call (`setLightboxUrl` → `onOpenLightbox`); the moved
 * block keeps its original indentation.
 */

import { sharedStyles } from '../FileDetail.styles';

export default function CaseScreenshotsSection({ file, screenshotsByIndex, onOpenLightbox }) {
  return (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>AtWork Confirmation Screenshots</h3>
              <div style={styles.caseScreenshotGrid}>
                {(file.caseLabels || []).map((label, idx) => {
                  const shot = screenshotsByIndex[idx];
                  if (!shot) return null;
                  return (
                    <div key={idx} style={styles.caseScreenshotCell}>
                      <img
                        src={shot.screenshotUrl}
                        alt={`AtWork confirmation for case ${idx + 1}`}
                        style={styles.caseScreenshotThumb}
                        onClick={() => onOpenLightbox(shot.screenshotUrl)}
                      />
                      <div style={styles.caseScreenshotMeta}>
                        <span style={styles.caseScreenshotTag}>Case {idx + 1}</span>
                        <span style={styles.caseScreenshotName}>{label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
  );
}

export const styles = {
  caseScreenshotGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '0.75rem',
  },
  caseScreenshotCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    padding: '0.6rem',
    background: '#F7F9FC',
    border: '1px solid var(--cp-surface-alt)',
    borderRadius: '8px',
  },
  caseScreenshotThumb: {
    width: '100%',
    maxHeight: '160px',
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid var(--cp-border)',
    cursor: 'pointer',
  },
  caseScreenshotMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  caseScreenshotTag: {
    alignSelf: 'flex-start',
    padding: '0.15rem 0.5rem',
    background: 'rgba(27, 154, 170, 0.12)',
    color: 'var(--cp-primary)',
    fontSize: '0.7rem',
    fontWeight: 600,
    borderRadius: '999px',
  },
  caseScreenshotName: {
    fontSize: '0.8rem',
    color: 'var(--cp-text)',
    fontWeight: 500,
  },
  // Shared with the FileDetail view (F7 seed, `FileDetail.styles.js`).
  section: sharedStyles.section,
  sectionTitle: sharedStyles.sectionTitle,
};
